import { Month } from '../types';
import { getCurrentLocale } from '../i18n/config';

export const CALENDAR_VIEWS = ['month', 'week', 'day', 'year'] as const;
export type CalendarView = typeof CALENDAR_VIEWS[number];

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

export const isDateOnlyValue = (value: string): boolean => DATE_ONLY_PATTERN.test(value);

export const formatDateToLocalISODate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Parse stored date values while preserving the intended local calendar day for
 * date-only strings such as `YYYY-MM-DD`.
 */
export const parseDateValuePreservingLocalDay = (value: string | Date): Date => {
  if (value instanceof Date) {
    return new Date(value.getTime());
  }

  const dateOnlyMatch = DATE_ONLY_PATTERN.exec(value);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }

  return new Date(value);
};

// CRITICAL FIX: getDayIndex must NOT use Math.abs() to allow negative values
// for dates before startDate. This fixes milestone position shifting bug.
export const getDayIndex = (date: Date, startDate: Date): number => {
  const oneDay = 1000 * 60 * 60 * 24;
  const normalizedDate = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  const normalizedStartDate = Date.UTC(
    startDate.getFullYear(),
    startDate.getMonth(),
    startDate.getDate()
  );
  // Calculate signed calendar-day difference - negative for dates before startDate
  return (normalizedDate - normalizedStartDate) / oneDay;
};

export function getMonthsInRange(startDate: Date, endDate: Date): Month[] {
  const start = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
  const end = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
  const months: Month[] = [];
  const locale = getCurrentLocale();
  
  while (start <= end) {
    const daysInMonth = new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();
    months.push({
      name: start.toLocaleString(locale, { month: 'long', year: 'numeric' }),
      days: daysInMonth,
      startDate: new Date(start),
    });
    start.setMonth(start.getMonth() + 1);
  }
  
  return months;
}
